import { CriteriaSegmentedControl } from '@genesislcap/foundation-criteria';
import {html, ref, repeat, sync, when} from '@genesislcap/web-core';
import { clearFilterIcon } from '../../../../mbm/components/icons';
import {BondBiddingRoute} from './bond-bidding';
import {customEvent} from '@genesislcap/foundation-events';
import {ColDef} from '@ag-grid-community/core';

CriteriaSegmentedControl;

export const BondBiddingTemplate = html<BondBiddingRoute>`
  <!-- Header with Filters button -->
  <div class="bond-bidding-header">
    <div class="controls">
      <rapid-label>Filters:</rapid-label>
      <rapid-button
        style="min-width: 200px; max-width: 220px; width: 220px;
         ${(x) => !x.hasClickedFilters ? 'animation: glow 2s ease-in-out infinite alternate;' : ''}
         background-color: color-mix(in srgb, var(--accent-fill-rest) 70%, var(--neutral-fill-rest));
         "
        @click=${(x) => {
          x.filtersFlyoutOpen = !x.filtersFlyoutOpen;
          x.hasClickedFilters = true;
        }}
        
      >
        <span class="truncate">
          ${(x) => x.filterName}
        </span>
        <rapid-icon
          name="gear"
          slot="end"
          style="font-size: 1.4em;"
        ></rapid-icon>
      </rapid-button>
      ${when((x) => !x.isPlatformActivityView, html<BondBiddingRoute>`
        <criteria-segmented-control
          :criteriaOptions=${(x) => x.watchlistOptions}
          :value=${sync((x) => x.watchlistValue)}
          ${ref('watchlistCriteria')}
        ></criteria-segmented-control>

        <criteria-segmented-control
          style="min-width: 125px;"
          :criteriaOptions=${(x) => x.bidModeCriteriaOptions}
          :value=${sync((x) => x.bidModeCriteriaValue)}
          ${ref('bidModeCriteria')}
        ></criteria-segmented-control>
      `)}

      <criteria-segmented-control
        style="min-width: 125px;"
        :criteriaOptions=${(x) => x.timeModeCriteriaOptions}
        :value=${sync((x) => x.timeModeCriteriaValue)}
        ${ref('timeModeCriteria')}
      ></criteria-segmented-control>

      <rapid-button style="font-weight: 600; min-width: 155px;" @click=${(x) => x.clearAllFilters()}>
        <span slot="start" style="display: inline-flex; align-items: center; margin-right: 8px;">
          ${clearFilterIcon}
        </span>
        Clear All Filters
      </rapid-button>
      ${when((x) => !x.isPlatformActivityView, html<BondBiddingRoute>`
          <div class="bulk-response-actions">
            <rapid-label>Bulk Response</rapid-label>
            <div class="upload-button-wrapper">
              <rapid-button
                class="bulk-response-btn"
                ?disabled=${(x) =>
                  x.isDownloadInProgress || x.isUploadInProgress || x.isSubmitAllInProgress}
                @click=${(x) => x.downloadAll()}
              >
                ${(x) => (x.isDownloadInProgress ? 'Downloading…' : 'Download All')}
              </rapid-button>
              <rapid-progress-ring
                class="upload-download-spinner"
                ?hidden=${(x) => !x.isDownloadInProgress}
              ></rapid-progress-ring>
            </div>
            <div class="upload-button-wrapper">
              <rapid-button
                class="bulk-response-btn"
                ?disabled=${(x) =>
                  x.isUploadInProgress || x.isDownloadInProgress || x.isSubmitAllInProgress}
                @click=${(x) => x.upload()}
              >
                ${(x) => (x.isUploadInProgress ? 'Uploading…' : 'Upload')}
              </rapid-button>
              <rapid-progress-ring
                class="upload-download-spinner"
                ?hidden=${(x) => !x.isUploadInProgress}
              ></rapid-progress-ring>
            </div>
            <div class="upload-button-wrapper">
              <rapid-button
                class="bulk-response-btn"
                ?disabled=${(x) =>
                  !x.hasEligibleBidsForSubmitAll ||
                  x.isUploadInProgress ||
                  x.isDownloadInProgress ||
                  x.isSubmitAllInProgress}
                @click=${(x) => x.submitAll()}
              >
                ${(x) => (x.isSubmitAllInProgress ? 'Submitting…' : 'Submit All')}
              </rapid-button>
              <rapid-progress-ring
                class="upload-download-spinner"
                ?hidden=${(x) => !x.isSubmitAllInProgress}
              ></rapid-progress-ring>
            </div>
          </div>
      `)}
      <input
        type="file"
        accept=".csv"
        style="display: none;"
        ${ref('uploadFileInput')}
        @change=${(x: any, c: any) => x.handleUploadFileSelected(c.event)}
      />
    </div>
  </div>

  <div class="bond-bidding-main-container">
    <div class=${(x) => `bond-bidding-grid-area${x.filtersFlyoutOpen ? ' flyout-open' : ''}`}>
      <div class="bond-bidding-flyout-area ${(x) => (x.filtersFlyoutOpen ? 'open' : 'closed')}">
        <div class="flyout-close-row">
          <rapid-button
            appearance="stealth"
            @click=${(x) => (x.filtersFlyoutOpen = false)}
            title="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 24px; height: 24px; color: var(--neutral-foreground-rest);">
              <path d="M18.3636 7.75736L16.9494 6.34315L11.2926 12L16.9494 17.6569L18.3636 16.2426L14.121 12L18.3636 7.75736Z" fill="currentColor"></path>
              <path d="M11.2926 6.34315L12.7068 7.75736L8.46417 12L12.7068 16.2426L11.2926 17.6568L5.63574 12L11.2926 6.34315Z" fill="currentColor"></path>
            </svg>
          </rapid-button>
        </div>
        <foundation-filters
          ${ref('foundationFilters')}
          design-system-prefix="rapid"
          :jsonSchema="${(x) => x.getBondBiddingJsonSchema}"
          :uischema="${(x) => x.getBondBiddingUiSchema}"
          show-filter-persistence-controls
          :value=${sync((x) => x.filterValue)}
          @search=${(x) => x.markFoundationFiltersSearchRequested()}
          @submit=${(x) => x.markFoundationFiltersSearchRequested()}
          @change=${(x, c) => x.handleFilterChange(customEvent(c))}
        ></foundation-filters>
      </div>
      <mbm-grid-pro
        ${ref('grid')}
        with-status-bar
        persist-column-state-key=${(x) => x.latestBiddingGridStateKey}
        enable-row-flashing
        enable-cell-flashing
        only-template-col-defs
      >
        ${repeat(
          (x) => x.liveActivityColumns,
          html<ColDef>`
            <grid-pro-column :definition="${(x) => x}"></grid-pro-column>
          `,
        )}
        ${when(
          (x) => x.isBondsCriteriaReady,
          html<BondBiddingRoute>`
            <grid-pro-genesis-datasource
              resource-name=${(x) => x.activeBondsResourceName}
              :deferredGridOptions=${(x) => x.gridOptions}
              criteria=${sync((x) => x.filterValue)}
              max-rows=${(x) => x.activeBondsMaxRows}
              max-view="15000"
              order-by=${(x) => x.activeBondsOrderBy}
              reverse
            ></grid-pro-genesis-datasource>
          `,
        )}
      </mbm-grid-pro>
    </div>
  </div>

  <rapid-flyout ${ref('bondFlyout')} :closed=${(x) => !x.bondDetailsOpen} @closed=${(x) => x.handleFlyoutClosed()}>
    <div slot="title">Bond Detail (${(x) => x.selectedBondId})</div>
    <tranche-detail-flyout
      ${ref('trancheDetail')}
      :flyoutElement=${(x) => x.bondFlyout}
      @close=${(x) => x.handleFlyoutClosed()}
    ></tranche-detail-flyout>
  </rapid-flyout>

  <rapid-flyout
    :closed=${(x) => !x.uploadValidationOpen}
    @closed=${(x) => x.handleUploadValidationClosed()}
  >
    <div slot="title">Upload Validation</div>
    <upload-validation-flyout ${ref('uploadValidationFlyout')}></upload-validation-flyout>
  </rapid-flyout>
`;
